/**
 * Shared property taxonomy, sourced from GET /api/taxonomy.
 * Composer, listing modal, and contact preferences all render from this
 * payload — categories, types (with subtypes), and attributes (with children).
 */
import { useEffect, useState } from 'react'
import { api } from '@/api/client'

export interface TaxonomyCategory {
  code: string
  label: string
  sort_order?: number
}

export interface TaxonomySubtype {
  code: string
  label: string
  sort_order?: number
}

export interface TaxonomyType {
  code: string
  label: string
  category_code: string
  sort_order?: number
  subtypes: TaxonomySubtype[]
}

export interface TaxonomyAllowedValue {
  code: string
  label: string
}

export type TaxonomyDataType = 'boolean' | 'single_choice' | 'multi_choice' | 'integer' | 'decimal'

export interface TaxonomyAttributeChild {
  code: string
  label: string
  data_type: TaxonomyDataType
  allowed_values: TaxonomyAllowedValue[] | null
  unit: string | null
  sort_order?: number
}

export interface TaxonomyAttribute {
  code: string
  label: string
  group_code: string
  data_type: TaxonomyDataType
  allowed_values: TaxonomyAllowedValue[] | null
  unit: string | null
  applies_to_categories: string[] | null
  sort_order?: number
  children: TaxonomyAttributeChild[]
}

export interface TaxonomyPayload {
  categories: TaxonomyCategory[]
  types: TaxonomyType[]
  attributes: TaxonomyAttribute[]
}

export type AttributeValue = boolean | number | string | string[]

export interface TaxonomyFormSlice {
  category: string
  property_type: string
  property_subtype: string
  other_label: string
  plot_area: string
  permissible_buildup_area: string
  site_coverage_percent: string
  permitted_floors: string
  zone_code: string
  attribute_values: Record<string, AttributeValue>
}

export interface ListingTaxonomyPayload {
  property_type: string
  property_subtype: string | null
  other_label: string | null
  attributes?: { code: string; value: AttributeValue }[]
  plot_area?: number | null
  permissible_buildup_area?: number | null
  site_coverage_percent?: number | null
  permitted_floors?: number | null
  zone_code?: string | null
}

export const EMPTY_TAXONOMY: TaxonomyPayload = {
  categories: [],
  types: [],
  attributes: [],
}

export const LAND_CATEGORY = 'land'

const GROUP_LABELS: Record<string, string> = {
  climate: 'Climate',
  wellness: 'Wellness & recreation',
  outdoor: 'Outdoor',
  service_rooms: 'Service rooms',
  kitchen: 'Kitchen',
  security: 'Security',
  access: 'Access',
  smart_home: 'Smart home',
  policy: 'Policy',
  parking: 'Parking',
}

/**
 * Saved attribute values from a listing payload -> the form's value map.
 *
 * `GET /api/properties/:id` returns `attributes: [{ code, value }]`, the same
 * shape the write path takes. Both edit surfaces hydrate through this so they
 * cannot disagree, and an absent or malformed field yields {} rather than
 * throwing on a page load.
 */
export function attributeValuesFromPayload(raw: unknown): Record<string, AttributeValue> {
  if (!Array.isArray(raw)) return {}
  const out: Record<string, AttributeValue> = {}
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const { code, value } = entry as { code?: unknown; value?: unknown }
    if (typeof code !== 'string' || !code) continue
    if (value == null) continue
    if (
      typeof value === 'boolean'
      || typeof value === 'number'
      || typeof value === 'string'
      || Array.isArray(value)
    ) {
      out[code] = value as AttributeValue
    }
  }
  return out
}

export function emptyTaxonomySlice(): TaxonomyFormSlice {
  return {
    category: 'residential',
    property_type: 'apartment',
    property_subtype: '',
    other_label: '',
    plot_area: '',
    permissible_buildup_area: '',
    site_coverage_percent: '',
    permitted_floors: '',
    zone_code: '',
    attribute_values: {},
  }
}

export function isOtherTypeCode(code: string): boolean {
  return code === 'other' || code.startsWith('other_')
}

export function typeIsOther(type: { code: string; label: string } | undefined): boolean {
  if (!type) return false
  return type.label.trim().toLowerCase() === 'other' || isOtherTypeCode(type.code)
}

export function categoryForType(taxonomy: TaxonomyPayload, propertyType: string): string | null {
  if (!propertyType) return null
  return taxonomy.types.find((t) => t.code === propertyType)?.category_code ?? null
}

export function typesForCategory(taxonomy: TaxonomyPayload, category: string): TaxonomyType[] {
  if (!category) return []
  return taxonomy.types.filter((t) => t.category_code === category)
}

export function resolvedCategory(slice: TaxonomyFormSlice, taxonomy: TaxonomyPayload): string {
  return slice.category || categoryForType(taxonomy, slice.property_type) || ''
}

function appliesToCategory(attr: TaxonomyAttribute, category: string): boolean {
  const cats = attr.applies_to_categories
  if (!cats || cats.length === 0) return true
  if (!category) return false
  return cats.includes(category)
}

export function groupLabel(groupCode: string): string {
  if (GROUP_LABELS[groupCode]) return GROUP_LABELS[groupCode]
  return groupCode.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export interface AttributeGroup {
  group_code: string
  label: string
  attributes: TaxonomyAttribute[]
}

export function attributesForCategory(taxonomy: TaxonomyPayload, category: string): AttributeGroup[] {
  const order: string[] = []
  const map = new Map<string, TaxonomyAttribute[]>()
  for (const attr of taxonomy.attributes) {
    if (!appliesToCategory(attr, category)) continue
    const key = attr.group_code || 'other'
    if (!map.has(key)) {
      map.set(key, [])
      order.push(key)
    }
    map.get(key)!.push(attr)
  }
  return order.map((group_code) => ({
    group_code,
    label: groupLabel(group_code),
    attributes: map.get(group_code)!,
  }))
}

function pruneAttributes(
  attributes: TaxonomyAttribute[],
  category: string,
  values: Record<string, AttributeValue>,
): Record<string, AttributeValue> {
  const allowed = new Set<string>()
  for (const attr of attributes) {
    if (!appliesToCategory(attr, category)) continue
    allowed.add(attr.code)
    if (values[attr.code] === true) {
      for (const child of attr.children || []) allowed.add(child.code)
    }
  }
  const next: Record<string, AttributeValue> = {}
  for (const [code, value] of Object.entries(values)) {
    if (allowed.has(code)) next[code] = value
  }
  return next
}

export function withCategory(
  slice: TaxonomyFormSlice,
  taxonomy: TaxonomyPayload,
  category: string,
): TaxonomyFormSlice {
  const types = typesForCategory(taxonomy, category)
  const keepType = types.some((t) => t.code === slice.property_type)
  const type = keepType ? types.find((t) => t.code === slice.property_type) : undefined
  const next: TaxonomyFormSlice = {
    ...slice,
    category,
    property_type: keepType ? slice.property_type : '',
    property_subtype:
      type && type.subtypes.some((s) => s.code === slice.property_subtype) ? slice.property_subtype : '',
    other_label: type && typeIsOther(type) ? slice.other_label : '',
    attribute_values: pruneAttributes(taxonomy.attributes, category, slice.attribute_values),
  }
  if (category !== LAND_CATEGORY) {
    next.plot_area = ''
    next.permissible_buildup_area = ''
    next.site_coverage_percent = ''
    next.permitted_floors = ''
    next.zone_code = ''
  }
  return next
}

export function withType(
  slice: TaxonomyFormSlice,
  taxonomy: TaxonomyPayload,
  propertyType: string,
): TaxonomyFormSlice {
  const type = taxonomy.types.find((t) => t.code === propertyType)
  return {
    ...slice,
    category: type?.category_code || slice.category,
    property_type: propertyType,
    property_subtype: type?.subtypes.some((s) => s.code === slice.property_subtype)
      ? slice.property_subtype
      : '',
    other_label: type && typeIsOther(type) ? slice.other_label : '',
  }
}

export function withSubtype(slice: TaxonomyFormSlice, propertySubtype: string): TaxonomyFormSlice {
  return { ...slice, property_subtype: propertySubtype }
}

export function withOtherLabel(slice: TaxonomyFormSlice, otherLabel: string): TaxonomyFormSlice {
  return { ...slice, other_label: otherLabel }
}

export function withLandField(
  slice: TaxonomyFormSlice,
  key: 'plot_area' | 'permissible_buildup_area' | 'site_coverage_percent' | 'permitted_floors' | 'zone_code',
  value: string,
): TaxonomyFormSlice {
  return { ...slice, [key]: value }
}

/** Boolean parent set to anything other than true drops its children. */
export function withAttribute(
  slice: TaxonomyFormSlice,
  attributes: TaxonomyAttribute[],
  code: string,
  value: AttributeValue,
): TaxonomyFormSlice {
  const nextValues: Record<string, AttributeValue> = { ...slice.attribute_values, [code]: value }
  const parent = attributes.find((a) => a.code === code)
  if (parent && parent.data_type === 'boolean' && value !== true) {
    for (const child of parent.children || []) delete nextValues[child.code]
  }
  return { ...slice, attribute_values: nextValues }
}

function numOrNull(raw: string): number | null {
  if (raw == null || String(raw).trim() === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

export function serializeAttributes(
  values: Record<string, AttributeValue>,
): { code: string; value: AttributeValue }[] | undefined {
  const writes: { code: string; value: AttributeValue }[] = []
  for (const [code, value] of Object.entries(values)) {
    if (value == null || value === '') continue
    if (Array.isArray(value) && value.length === 0) continue
    // A trailing-dot string is a mid-typing numeric artifact ('12.'), held as
    // text by the numeric input so it does not rewrite what the user typed.
    // It is not a value: Number('12.') would be 12 here and Number('.') NaN,
    // so it is dropped rather than guessed at.
    if (typeof value === 'string' && value.endsWith('.')) continue
    writes.push({ code, value })
  }
  return writes.length ? writes : undefined
}

/**
 * Listing write fragment. `property_type` is always the registry code.
 * A typed suggestion is `other_label`, never the type.
 * Land columns are included only for category = land.
 */
export function listingTaxonomyPayload(slice: TaxonomyFormSlice): ListingTaxonomyPayload {
  const payload: ListingTaxonomyPayload = {
    property_type: slice.property_type,
    property_subtype: slice.property_subtype.trim() || null,
    other_label:
      isOtherTypeCode(slice.property_type) && slice.other_label.trim()
        ? slice.other_label.trim()
        : null,
  }
  const attributes = serializeAttributes(slice.attribute_values)
  if (attributes) payload.attributes = attributes
  if (slice.category === LAND_CATEGORY) {
    payload.plot_area = numOrNull(slice.plot_area)
    payload.permissible_buildup_area = numOrNull(slice.permissible_buildup_area)
    payload.site_coverage_percent = numOrNull(slice.site_coverage_percent)
    payload.permitted_floors = numOrNull(slice.permitted_floors)
    payload.zone_code = slice.zone_code.trim() || null
  }
  return payload
}

export async function submitTypeSuggestion(slice: TaxonomyFormSlice, listingId: string): Promise<void> {
  if (!listingId || !isOtherTypeCode(slice.property_type)) return
  const label = slice.other_label.trim()
  if (!label) return
  if (typeof api.suggestTaxonomy !== 'function') return
  await api.suggestTaxonomy({
    kind: 'type',
    suggested_label: label,
    context_code: slice.category || null,
    listing_id: listingId,
  })
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function normalizeAllowed(value: unknown): TaxonomyAllowedValue[] | null {
  if (!Array.isArray(value)) return null
  return value.map((entry) => {
    if (typeof entry === 'string') return { code: entry, label: entry }
    const row = asRecord(entry)
    const code = asString(row?.code)
    return { code, label: asString(row?.label, code) }
  }).filter((entry) => entry.code)
}

function normalizeChild(value: unknown): TaxonomyAttributeChild | null {
  const row = asRecord(value)
  if (!row || typeof row.code !== 'string') return null
  return {
    code: row.code,
    label: asString(row.label, row.code),
    data_type: (asString(row.data_type, 'boolean') as TaxonomyDataType),
    allowed_values: normalizeAllowed(row.allowed_values),
    unit: typeof row.unit === 'string' ? row.unit : null,
    sort_order: typeof row.sort_order === 'number' ? row.sort_order : undefined,
  }
}

function normalizeAttribute(value: unknown): TaxonomyAttribute | null {
  const row = asRecord(value)
  if (!row || typeof row.code !== 'string') return null
  const children = Array.isArray(row.children)
    ? row.children.map(normalizeChild).filter((c): c is TaxonomyAttributeChild => c != null)
    : []
  const cats = Array.isArray(row.applies_to_categories)
    ? row.applies_to_categories.filter((c): c is string => typeof c === 'string')
    : null
  const attr: TaxonomyAttribute = {
    code: row.code,
    label: asString(row.label, row.code),
    group_code: asString(row.group_code, 'other'),
    data_type: (asString(row.data_type, 'boolean') as TaxonomyDataType),
    allowed_values: normalizeAllowed(row.allowed_values),
    unit: typeof row.unit === 'string' ? row.unit : null,
    applies_to_categories: cats,
    children,
  }
  if (typeof row.sort_order === 'number') attr.sort_order = row.sort_order
  return attr
}

function normalizeType(value: unknown): TaxonomyType | null {
  const row = asRecord(value)
  if (!row || typeof row.code !== 'string') return null
  const subtypes = Array.isArray(row.subtypes)
    ? row.subtypes
        .map((s): TaxonomySubtype | null => {
          const sub = asRecord(s)
          if (!sub || typeof sub.code !== 'string') return null
          const subtype: TaxonomySubtype = {
            code: sub.code,
            label: asString(sub.label, sub.code),
          }
          if (typeof sub.sort_order === 'number') subtype.sort_order = sub.sort_order
          return subtype
        })
        .filter((s): s is TaxonomySubtype => s != null)
    : []
  const type: TaxonomyType = {
    code: row.code,
    label: asString(row.label, row.code),
    category_code: asString(row.category_code),
    subtypes,
  }
  if (typeof row.sort_order === 'number') type.sort_order = row.sort_order
  return type
}

export function normalizeTaxonomy(raw: unknown): TaxonomyPayload {
  const row = asRecord(raw)
  if (!row) return EMPTY_TAXONOMY
  return {
    categories: Array.isArray(row.categories)
      ? row.categories
          .map((c): TaxonomyCategory | null => {
            const cat = asRecord(c)
            if (!cat || typeof cat.code !== 'string') return null
            const category: TaxonomyCategory = {
              code: cat.code,
              label: asString(cat.label, cat.code),
            }
            if (typeof cat.sort_order === 'number') category.sort_order = cat.sort_order
            return category
          })
          .filter((c): c is TaxonomyCategory => c != null)
      : [],
    types: Array.isArray(row.types)
      ? row.types.map(normalizeType).filter((t): t is TaxonomyType => t != null)
      : [],
    attributes: Array.isArray(row.attributes)
      ? row.attributes.map(normalizeAttribute).filter((a): a is TaxonomyAttribute => a != null)
      : [],
  }
}

let cached: TaxonomyPayload | null = null
let inflight: Promise<TaxonomyPayload> | null = null

export function resetPropertyTaxonomyCache(): void {
  cached = null
  inflight = null
}

export function loadPropertyTaxonomy(): Promise<TaxonomyPayload> {
  if (cached) return Promise.resolve(cached)
  if (!inflight) {
    inflight = (async () => {
      if (typeof api.getTaxonomy !== 'function') {
        throw new Error('getTaxonomy is unavailable')
      }
      const payload = normalizeTaxonomy(await api.getTaxonomy())
      cached = payload
      return payload
    })().finally(() => {
      inflight = null
    })
  }
  return inflight
}

export type TaxonomyStatus = 'loading' | 'ready' | 'error'

export function usePropertyTaxonomy(): { taxonomy: TaxonomyPayload; status: TaxonomyStatus } {
  const [taxonomy, setTaxonomy] = useState<TaxonomyPayload>(cached ?? EMPTY_TAXONOMY)
  const [status, setStatus] = useState<TaxonomyStatus>(cached ? 'ready' : 'loading')

  useEffect(() => {
    let cancelled = false
    if (typeof api.getTaxonomy !== 'function') {
      setStatus('error')
      return
    }
    if (!cached) setStatus('loading')
    loadPropertyTaxonomy()
      .then((payload) => {
        if (cancelled) return
        setTaxonomy(payload)
        setStatus('ready')
      })
      .catch(() => {
        if (cancelled) return
        setTaxonomy(EMPTY_TAXONOMY)
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { taxonomy, status }
}
